import React from 'react'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MdAdd, MdDeleteOutline, MdDragIndicator, MdEdit } from 'react-icons/md'
import TaskPage2024Task from '../TaskPage2024Task/TaskPage2024Task'

const TaskPage2024Column = ({
  column,
  disabled,
  onAddTask,
  onEditTask,
  onDeleteTask,
  onRenameColumn,
  onDeleteColumn,
  canDelete,
}) => {
  const [editingName, setEditingName] = React.useState(false)
  const [nameDraft, setNameDraft] = React.useState(column.name)

  React.useEffect(() => {
    if (!editingName) setNameDraft(column.name)
  }, [column.name, editingName])

  const sortable = useSortable({
    id: column.id,
    data: { type: 'column', column, columnId: column.id },
    disabled,
  })
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  }

  const finishRename = async () => {
    const name = nameDraft.trim()
    if (!name) {
      setNameDraft(column.name)
      setEditingName(false)
      return
    }
    const saved = await onRenameColumn(column.id, name)
    if (saved) setEditingName(false)
  }

  return (
    <section
      ref={sortable.setNodeRef}
      style={style}
      className={`task-board-column ${sortable.isOver ? 'task-board-column--over' : ''} ${sortable.isDragging ? 'task-board-column--dragging' : ''}`}
    >
      <header className="task-board-column__header">
        <div>
          <button
            type="button"
            className="task-board-column__drag-handle"
            aria-label={`Move ${column.name} column`}
            title="Drag to move column"
            disabled={disabled || editingName}
            {...sortable.attributes}
            {...sortable.listeners}
          >
            <MdDragIndicator />
          </button>
          <span className={`task-board-column__dot task-board-column__dot--${column.id}`} />
          {editingName ? (
            <input
              autoFocus
              className="task-board-column__name-input"
              aria-label={`Rename ${column.name} column`}
              maxLength="80"
              value={nameDraft}
              onChange={(event) => setNameDraft(event.target.value)}
              onBlur={finishRename}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  finishRename()
                }
                if (event.key === 'Escape') {
                  setNameDraft(column.name)
                  setEditingName(false)
                }
              }}
              disabled={disabled}
            />
          ) : (
            <button
              type="button"
              className="task-board-column__name-button"
              aria-label={`Rename ${column.name} column`}
              title="Rename column"
              onClick={() => setEditingName(true)}
              disabled={disabled}
            >
              <h2>{column.name}</h2>
              <MdEdit />
            </button>
          )}
        </div>
        <div className="task-board-column__actions">
          <span className="task-board-column__count">{column.cards.length}</span>
          <button
            type="button"
            className="task-board-column__delete"
            aria-label={`Delete ${column.name} column`}
            title={canDelete ? 'Delete column' : 'The board must have at least one column'}
            onClick={() => onDeleteColumn(column.id)}
            disabled={disabled || !canDelete || editingName}
          >
            <MdDeleteOutline />
          </button>
        </div>
      </header>

      <div className="task-board-column__cards">
        <SortableContext items={column.cards.map((card) => card.id)} strategy={verticalListSortingStrategy}>
          {column.cards.map((card) => (
            <TaskPage2024Task
              key={card.id}
              card={{ ...card, columnId: column.id }}
              disabled={disabled}
              onEdit={() => onEditTask(card, column.id)}
              onDelete={() => onDeleteTask(card)}
            />
          ))}
        </SortableContext>
        {column.cards.length === 0 && (
          <div className="task-board-column__empty">Drop a task here</div>
        )}
      </div>

      <button type="button" className="task-board-column__add" onClick={() => onAddTask(column.id)} disabled={disabled}>
        <MdAdd /> Add task
      </button>
    </section>
  )
}

export default TaskPage2024Column
